<template>
  <v-navigation-drawer
    :model-value="modelValue"
    location="right"
    temporary
    width="820"
    class="ncrm-staging-drawer"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="d-flex flex-column fill-height">

      <!-- ── Header ──────────────────────────────────────────────────────────── -->
      <div class="ncrm-staging-drawer__header pa-4">
        <div class="d-flex align-start justify-space-between ga-2">
          <div class="min-w-0">
            <div class="text-h6 ncrm-text-strong">
              {{ record?.name || record?.external_id || $t('ncrm.workflows.staging.drawer.unknownId') }}
            </div>
            <div v-if="record?.name && record?.external_id" class="text-caption text-medium-emphasis mt-1">
              {{ record.external_id }}
              <span v-if="record.source_system" class="ml-1">· {{ record.source_system }}</span>
            </div>
            <div class="d-flex flex-wrap align-center ga-2 mt-2">
              <v-chip v-if="record?.status" size="x-small"
                :color="statusChip(record.status).color" :variant="statusChip(record.status).variant">
                {{ $t(`ncrm.workflows.staging.status.${record.status}`) }}
              </v-chip>
              <v-chip v-if="record?.mapping_status" size="x-small"
                :color="mappingChip(record.mapping_status).color" :variant="mappingChip(record.mapping_status).variant">
                {{ $t(`ncrm.workflows.staging.mappingStatus.${record.mapping_status}`) }}
              </v-chip>
              <v-chip v-if="record?.dedup_score !== null && record?.dedup_score !== undefined"
                size="x-small" :color="dedupScoreColor(record.dedup_score)">
                Dedup: {{ record.dedup_score }}%
              </v-chip>
            </div>
          </div>
          <div class="d-flex align-center ga-2 flex-shrink-0">
            <v-btn
              v-if="canOpenWrittenCompany"
              color="primary"
              variant="tonal"
              size="small"
              @click="openWrittenCompany"
            >
              <v-icon start>mdi-domain</v-icon>
              {{ $t('ncrm.workflows.staging.drawer.openCompany') }}
            </v-btn>
            <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('update:modelValue', false)" />
          </div>
        </div>
      </div>
      <v-divider />

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <!-- ── Body ───────────────────────────────────────────────────────────── -->
      <div class="flex-grow-1 overflow-y-auto">
        <template v-if="record">
          <v-alert
            v-if="isReadOnly"
            type="success"
            density="compact"
            class="ma-4 mb-0 text-body-2"
          >
            {{ $t('ncrm.workflows.staging.drawer.writtenReadonly') }}
          </v-alert>

          <!-- ── Dedup candidates ──────────────────────────────────────────── -->
          <template v-if="dedupCandidates.length">
            <div class="ncrm-staging-drawer__section pa-4 pb-3">
              <div class="text-caption font-weight-semibold text-medium-emphasis mb-2 text-uppercase">
                {{ $t('ncrm.workflows.staging.drawer.dedupCandidates') }}
              </div>
              <div class="d-flex flex-column ga-1">
                <div
                  v-for="c in dedupCandidates"
                  :key="c.id"
                  class="ncrm-staging-drawer__candidate"
                  :class="{
                    'ncrm-staging-drawer__candidate--selected': selectedCandidateId === c.id,
                    'ncrm-staging-drawer__candidate--readonly': isReadOnly,
                  }"
                  :tabindex="isReadOnly ? -1 : 0"
                  @click="selectCandidate(c.id)"
                  @keyup.enter="selectCandidate(c.id)"
                >
                  <div class="d-flex align-center justify-space-between ga-2">
                    <div class="d-flex align-center ga-2 min-w-0">
                      <v-icon
                        size="18"
                        :color="selectedCandidateId === c.id ? 'primary' : 'medium-emphasis'"
                      >
                        {{ selectedCandidateId === c.id ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                      </v-icon>
                      <span class="font-weight-medium text-body-2 text-truncate">
                        {{ c.name || c.external_id }}
                      </span>
                    </div>
                    <v-chip size="x-small" :color="dedupScoreColor(c.score)">
                      {{ c.score }}%
                    </v-chip>
                  </div>
                  <div v-if="candidateEvidence(c).length" class="ncrm-staging-drawer__candidate-evidence">
                    <div
                      v-for="field in candidateEvidence(c)"
                      :key="field.key"
                      class="ncrm-staging-drawer__candidate-field"
                    >
                      <div class="d-flex align-center justify-space-between ga-2">
                        <span class="ncrm-staging-drawer__candidate-field-label">
                          {{ field.label }}
                        </span>
                        <span class="ncrm-staging-drawer__candidate-field-score">
                          {{ field.score }}% · {{ $t('ncrm.workflows.staging.drawer.contribution') }} {{ field.contribution }}%
                        </span>
                      </div>
                      <div class="ncrm-staging-drawer__candidate-values">
                        <span>{{ field.stagingField }}: {{ field.stagingValue }}</span>
                        <v-icon size="11" color="medium-emphasis">mdi-arrow-right</v-icon>
                        <span>{{ field.companyField }}: {{ field.companyValue }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="ncrm-staging-drawer__candidate-empty">
                    {{ $t('ncrm.workflows.staging.drawer.noMatchEvidence') }}
                  </div>
                </div>
              </div>
            </div>
            <v-divider />
          </template>

          <!-- ── AI enrichment suggestions ───────────────────────────────── -->
          <template v-if="enrichmentSuggestionRows.length">
            <div class="ncrm-staging-drawer__section pa-4 pb-3">
              <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-2">
                <div>
                  <div class="text-caption font-weight-semibold text-medium-emphasis text-uppercase">
                    {{ $t('ncrm.workflows.staging.drawer.aiSuggestions') }}
                  </div>
                  <div class="text-body-2 text-medium-emphasis mt-1">
                    {{ $t('ncrm.workflows.staging.drawer.aiSuggestionsHint') }}
                  </div>
                </div>
                <v-chip size="x-small" color="info">
                  {{ enrichmentSuggestionRows.length }}
                </v-chip>
              </div>

              <div class="d-flex flex-column ga-2">
                <div
                  v-for="suggestion in enrichmentSuggestionRows"
                  :key="suggestion.field"
                  class="ncrm-staging-drawer__ai-suggestion"
                  :class="{ 'ncrm-staging-drawer__ai-suggestion--readonly': isReadOnly }"
                >
                  <div class="d-flex align-start justify-space-between ga-2">
                    <v-checkbox-btn
                      v-model="suggestionSelections[suggestion.field]"
                      :disabled="isReadOnly || saving"
                      class="mt-1"
                    />
                    <div class="min-w-0 flex-grow-1">
                      <div class="d-flex flex-wrap align-center ga-2">
                        <span class="ncrm-staging-drawer__ai-field">
                          {{ suggestion.label }}
                        </span>
                        <v-chip size="x-small" color="info">
                          {{ suggestion.confidenceLabel }}
                        </v-chip>
                        <v-chip
                          v-if="suggestion.decision"
                          size="x-small"
                          :color="suggestion.decision === 'approved' ? 'success' : 'error'"
                        >
                          {{ $t(`ncrm.workflows.staging.drawer.aiDecision.${suggestion.decision}`) }}
                        </v-chip>
                      </div>
                      <div class="ncrm-staging-drawer__ai-values mt-2">
                        <div>
                          <span class="ncrm-staging-drawer__ai-value-label">
                            {{ $t('ncrm.workflows.staging.drawer.currentValue') }}
                          </span>
                          <span class="ncrm-staging-drawer__ai-value">
                            {{ suggestion.currentValueLabel }}
                          </span>
                        </div>
                        <v-icon size="14" color="medium-emphasis">mdi-arrow-right</v-icon>
                        <v-text-field
                          v-model="suggestionEdits[suggestion.field]"
                          :label="$t('ncrm.workflows.staging.drawer.aiSuggestedValue')"
                          density="compact"
                          hide-details
                          :readonly="isReadOnly"
                          :disabled="isReadOnly || saving"
                        />
                      </div>
                      <div v-if="suggestion.reason" class="ncrm-staging-drawer__ai-reason mt-2">
                        {{ suggestion.reason }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!isReadOnly" class="d-flex flex-wrap ga-2 justify-end mt-3">
                <v-btn
                  color="error"
                  variant="tonal"
                  size="small"
                  :disabled="!hasSelectedAiSuggestions || saving"
                  :loading="saving"
                  @click="submitAiSuggestionDecisions('rejected')"
                >
                  <v-icon start>mdi-close-circle-outline</v-icon>
                  {{ $t('ncrm.workflows.staging.drawer.rejectSelectedAi') }}
                </v-btn>
                <v-btn
                  color="success"
                  variant="tonal"
                  size="small"
                  :disabled="!hasSelectedAiSuggestions || saving"
                  :loading="saving"
                  @click="submitAiSuggestionDecisions('approved')"
                >
                  <v-icon start>mdi-check-circle-outline</v-icon>
                  {{ $t('ncrm.workflows.staging.drawer.applySelectedAi') }}
                </v-btn>
              </div>
            </div>
            <v-divider />
          </template>

          <template v-if="postEnrichmentDedupMeta">
            <div class="ncrm-staging-drawer__section pa-4 pb-3">
              <v-alert
                :type="postEnrichmentDedupAlertType"
                density="compact"
                class="text-body-2"
              >
                {{ postEnrichmentDedupText }}
              </v-alert>
            </div>
            <v-divider />
          </template>

          <!-- ── Two-column: fields + mapping audit ────────────────────────── -->
          <div class="ncrm-staging-drawer__columns">

            <!-- Left: all staging fields grouped ─────────────────────────── -->
            <div class="ncrm-staging-drawer__col pa-4">
              <div class="text-caption font-weight-semibold text-medium-emphasis mb-3 text-uppercase">
                {{ $t('ncrm.workflows.staging.drawer.allFields') }}
              </div>

              <div v-for="group in fieldGroups" :key="group.label" class="mb-3">
                <div class="text-caption text-medium-emphasis mb-1 font-weight-medium">
                  {{ group.label }}
                </div>
                <div v-for="f in group.fields" :key="f.key" class="ncrm-staging-drawer__field">
                  <span class="ncrm-staging-drawer__field-key">{{ f.key }}</span>
                  <span v-if="f.value !== null" class="ncrm-staging-drawer__field-value">{{ f.value }}</span>
                  <span v-else class="ncrm-staging-drawer__field-empty">—</span>
                </div>
              </div>
            </div>

            <!-- Right: mapping audit (source → crm column, editabil) ───── -->
            <div class="ncrm-staging-drawer__col pa-4">
              <div class="text-caption font-weight-semibold text-medium-emphasis mb-3 text-uppercase">
                {{ $t('ncrm.workflows.staging.drawer.mappingAudit') }}
              </div>

              <template v-if="mappingAuditEntries.length">
                <div v-for="entry in mappingAuditEntries" :key="entry.source"
                  class="ncrm-staging-drawer__mapping-edit-row">
                  <span class="ncrm-staging-drawer__field-key ncrm-staging-drawer__mapping-src">
                    {{ entry.source }}
                  </span>
                  <v-icon size="12" color="medium-emphasis" class="mx-1 flex-shrink-0">mdi-arrow-right</v-icon>
                  <v-select
                    v-model="mappingChanges[entry.source]"
                    :items="crmFieldOptions"
                    item-title="label"
                    item-value="value"
                    density="compact"
                    variant="plain"
                    hide-details
                    :placeholder="entry.target"
                    :menu-props="{ maxHeight: 320, zIndex: 2710 }"
                    :readonly="isReadOnly"
                    :disabled="isReadOnly"
                    class="ncrm-staging-drawer__mapping-select"
                  />
                </div>
              </template>
              <div v-else class="text-caption text-medium-emphasis">
                {{ $t('ncrm.workflows.staging.drawer.noMappingAudit') }}
              </div>

              <!-- Buton salvare re-mapare câmpuri deja mapate -->
              <v-btn v-if="!isReadOnly && hasMappingChanges" color="primary" variant="tonal" size="small"
                class="mt-3" :loading="saving" @click="submitAllRemaps">
                <v-icon start>mdi-content-save-outline</v-icon>
                {{ $t('ncrm.workflows.staging.drawer.saveMappingChanges') }}
              </v-btn>
            </div>

          </div>
          <v-divider />

          <!-- ── Notes (full width) ─────────────────────────────────────────── -->
          <template v-if="record.notes">
            <div class="ncrm-staging-drawer__section pa-4 pb-3">
              <div class="text-caption font-weight-semibold text-medium-emphasis mb-2 text-uppercase">
                {{ $t('ncrm.workflows.staging.drawer.notes') }}
              </div>
              <div class="ncrm-staging-drawer__notes-content text-body-2">{{ record.notes }}</div>
            </div>
            <v-divider />
          </template>

          <!-- ── Unmapped fields ────────────────────────────────────────────── -->
          <template v-if="unmappedEntries.length">
            <div class="ncrm-staging-drawer__section pa-4">
              <div class="d-flex align-center ga-2 mb-2">
                <div class="text-caption font-weight-semibold text-medium-emphasis text-uppercase">
                  {{ $t('ncrm.workflows.staging.drawer.unmappedFields') }}
                </div>
                <v-chip size="x-small" color="warning">{{ unmappedEntries.length }}</v-chip>
              </div>
              <v-alert type="info" density="compact" class="mb-3 text-body-2">
                {{ $t('ncrm.workflows.staging.drawer.unmappedHint') }}
              </v-alert>
              <div class="d-flex flex-column ga-2">
                <div v-for="entry in unmappedEntries" :key="entry.key"
                  class="ncrm-staging-drawer__unmapped-row">
                  <div class="ncrm-staging-drawer__unmapped-key">
                    <span class="text-caption font-weight-medium">{{ entry.key }}</span>
                    <span class="text-caption text-medium-emphasis ml-2">{{ entry.value }}</span>
                  </div>
                  <v-select
                    v-model="remapSelections[entry.key]"
                    :items="crmFieldOptions"
                    item-title="label"
                    item-value="value"
                    :placeholder="$t('ncrm.workflows.staging.drawer.remapPlaceholder')"
                    density="compact"
                    hide-details
                    :clearable="!isReadOnly"
                    :readonly="isReadOnly"
                    :disabled="isReadOnly"
                    :menu-props="{ maxHeight: 320, zIndex: 2710 }"
                    class="ncrm-staging-drawer__unmapped-select"
                  />
                </div>
              </div>
              <v-btn v-if="!isReadOnly && hasRemapSelections" color="primary" variant="tonal" size="small"
                class="mt-3" :loading="saving" @click="submitRemap">
                <v-icon start>mdi-check</v-icon>
                {{ $t('ncrm.workflows.staging.drawer.applyRemap') }}
              </v-btn>
            </div>
            <v-divider />
          </template>

          <!-- ── Review note input ──────────────────────────────────────────── -->
          <div class="ncrm-staging-drawer__section pa-4">
            <v-textarea
              v-model="notesInput"
              :label="$t('ncrm.workflows.staging.drawer.addNote')"
              rows="2"
              hide-details
              auto-grow
              :readonly="isReadOnly"
            />
          </div>

          <!-- ── Raw JSON (collapsible) ─────────────────────────────────────── -->
          <v-divider />
          <div class="ncrm-staging-drawer__section pa-4 pb-3">
            <div class="d-flex align-center justify-space-between cursor-pointer"
              @click="showRawJson = !showRawJson">
              <div class="text-caption font-weight-semibold text-medium-emphasis text-uppercase">
                {{ $t('ncrm.workflows.staging.drawer.rawJson') }}
              </div>
              <v-icon size="16">{{ showRawJson ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <div v-if="showRawJson" class="mt-2">
              <pre v-if="rawJsonFormatted" class="ncrm-staging-drawer__raw-pre">{{ rawJsonFormatted }}</pre>
              <div v-else class="text-caption text-medium-emphasis">
                {{ $t('ncrm.workflows.staging.drawer.noRawData') }}
              </div>
            </div>
          </div>

        </template>
      </div>

      <!-- ── Actions footer ─────────────────────────────────────────────────── -->
      <v-divider />
      <div class="ncrm-staging-drawer__footer pa-4">
        <div class="d-flex flex-wrap ga-2 justify-end">
          <v-btn v-if="canRunDedup" variant="tonal" color="info"
            :loading="saving" @click="$emit('run-dedup', { record })">
            <v-icon start>mdi-shuffle-variant</v-icon>
            {{ $t('ncrm.workflows.staging.actions.runDedup') }}
          </v-btn>
          <v-btn v-if="canRecheckDedupAfterEnrichment" variant="tonal" color="warning"
            :loading="saving" @click="$emit('recheck-dedup', { record })">
            <v-icon start>mdi-refresh</v-icon>
            {{ $t('ncrm.workflows.staging.actions.recheckDedupAfterEnrichment') }}
          </v-btn>
          <v-btn v-if="canReview" variant="tonal" color="error"
            :loading="saving" @click="submitAction('rejected')">
            <v-icon start>mdi-close-circle-outline</v-icon>
            {{ $t('ncrm.workflows.staging.actions.rejectEntry') }}
          </v-btn>
          <v-btn v-if="canConfirmNewCompany" variant="tonal" color="success"
            :loading="saving" @click="submitAction('approved-new')">
            <v-icon start>mdi-check-circle-outline</v-icon>
            {{ $t('ncrm.workflows.staging.actions.confirmNewCompany') }}
          </v-btn>
          <v-btn v-if="canConfirmExistingCompany" variant="tonal" color="warning"
            :loading="saving" :disabled="!selectedCandidateId" @click="submitAction('approved-existing')">
            <v-icon start>mdi-link-variant</v-icon>
            {{ $t('ncrm.workflows.staging.actions.confirmExistingCompany') }}
          </v-btn>
          <v-btn v-if="canWrite" color="primary" variant="flat"
            :loading="saving" @click="$emit('write', { record })">
            <v-icon start>mdi-database-arrow-up-outline</v-icon>
            {{ writeActionLabel }}
          </v-btn>
        </div>
      </div>

    </div>
  </v-navigation-drawer>
</template>

<script>
import { buildCrmRemapOptions, CRM_REMAP_COLUMNS, FIELD_GROUPS } from "./NcrmWorkflowStagingFields.mjs";

export default {
  name: "NcrmWorkflowStagingDrawer",
  props: {
    modelValue: { type: Boolean, default: false },
    record:     { type: Object, default: null },
    loading:    { type: Boolean, default: false },
    saving:     { type: Boolean, default: false },
  },
  emits: [
    "update:modelValue", "approve", "reject", "run-dedup", "recheck-dedup",
    "write", "remap", "review-enrichment",
  ],
  data() {
    return {
      // { sourceField: targetCrmCol } pentru câmpuri nemapate
      remapSelections: {},
      // { sourceField: newTargetCrmCol } pentru câmpuri deja mapate (re-mapare)
      mappingChanges: {},
      suggestionSelections: {},
      suggestionEdits: {},
      selectedCandidateId: null,
      notesInput: "",
      showRawJson: false,
    };
  },
  computed: {
    fieldGroups() {
      if (!this.record) return [];
      return FIELD_GROUPS
        .map((group) => ({
          label: this.$t(group.labelKey),
          fields: group.fields.map((key) => ({
            key,
            value: this.formatFieldValue(key, this.record[key]),
          })),
        }))
        .filter((group) => group.fields.some((f) => f.value !== null));
    },
    mappingAuditEntries() {
      if (!this.record?.mapping_applied_json) return [];
      let parsed = {};
      try {
        parsed = typeof this.record.mapping_applied_json === "string"
          ? JSON.parse(this.record.mapping_applied_json)
          : this.record.mapping_applied_json;
      } catch (_) { /* ignore */ }
      return Object.entries(parsed || {}).map(([source, target]) => ({ source, target }));
    },
    unmappedEntries() {
      if (!this.record?.unmapped_fields_json) return [];
      let parsed = [];
      try {
        parsed = typeof this.record.unmapped_fields_json === "string"
          ? JSON.parse(this.record.unmapped_fields_json)
          : this.record.unmapped_fields_json;
      } catch (_) { /* ignore */ }
      return (Array.isArray(parsed) ? parsed : []).map((item) => ({
        key: item.field ?? item.key ?? "?",
        value: item.value != null ? String(item.value) : "—",
      }));
    },
    dedupCandidates() {
      if (!this.record?.dedup_candidates_json) return [];
      try {
        const c = typeof this.record.dedup_candidates_json === "string"
          ? JSON.parse(this.record.dedup_candidates_json)
          : this.record.dedup_candidates_json;
        return Array.isArray(c) ? c : [];
      } catch (_) { return []; }
    },
    enrichmentState() {
      if (!this.record?.enrichment_json) return {};
      try {
        return typeof this.record.enrichment_json === "string"
          ? JSON.parse(this.record.enrichment_json)
          : this.record.enrichment_json;
      } catch (_) {
        return {};
      }
    },
    enrichmentReviewDecisions() {
      const decisions = this.enrichmentState?.review?.decisions;
      return decisions && typeof decisions === "object" ? decisions : {};
    },
    postEnrichmentDedupMeta() {
      const meta = this.enrichmentState?.review?.post_enrichment_dedup;
      return meta && typeof meta === "object" ? meta : null;
    },
    postEnrichmentDedupAlertType() {
      if (this.postEnrichmentDedupMeta?.status === "failed") return "warning";
      const decision = this.postEnrichmentDedupMeta?.dedup_decision_status;
      if (decision === "duplicate_found" || decision === "pending_review") return "warning";
      return "info";
    },
    postEnrichmentDedupText() {
      const meta = this.postEnrichmentDedupMeta || {};
      if (meta.status === "failed") {
        return this.$t("ncrm.workflows.staging.drawer.postEnrichmentDedupFailed", {
          error: meta.error || "-",
        });
      }
      return this.$t("ncrm.workflows.staging.drawer.postEnrichmentDedupCompleted", {
        score: meta.dedup_score ?? "-",
        candidates: meta.candidates_count ?? 0,
      });
    },
    enrichmentSuggestionRows() {
      const byField = new Map();
      const addSuggestion = (raw = {}) => {
        const field = String(raw.field || raw.field_key || "").trim();
        if (!field || !CRM_REMAP_COLUMNS.includes(field)) return;
        const hasValue = Object.prototype.hasOwnProperty.call(raw, "value") ||
          Object.prototype.hasOwnProperty.call(raw, "suggested_value");
        const value = hasValue ? (raw.value ?? raw.suggested_value) : undefined;
        if (value === undefined || value === null || value === "") return;
        if (byField.has(field)) return;
        const decision = this.enrichmentReviewDecisions[field]?.status || null;
        byField.set(field, {
          field,
          label: field,
          value,
          valueLabel: this.formatSuggestionValue(value),
          currentValueLabel: this.formatFieldValue(field, this.record?.[field]) || "—",
          confidence: Number.isFinite(Number(raw.confidence)) ? Number(raw.confidence) : null,
          confidenceLabel: this.formatConfidence(raw.confidence),
          reason: raw.reason ? String(raw.reason) : "",
          decision,
        });
      };

      const fieldSuggestions = Array.isArray(this.enrichmentState?.field_suggestions)
        ? this.enrichmentState.field_suggestions
        : [];
      fieldSuggestions.forEach(addSuggestion);

      const suggestedValues = this.enrichmentState?.suggested_values &&
        typeof this.enrichmentState.suggested_values === "object"
        ? this.enrichmentState.suggested_values
        : {};
      Object.entries(suggestedValues).forEach(([field, value]) => addSuggestion({ field, value }));

      return [...byField.values()];
    },
    hasSelectedAiSuggestions() {
      return this.enrichmentSuggestionRows.some((suggestion) => this.suggestionSelections[suggestion.field]);
    },
    rawJsonFormatted() {
      if (!this.record?.raw_source_json) return null;
      try {
        const parsed = typeof this.record.raw_source_json === "string"
          ? JSON.parse(this.record.raw_source_json)
          : this.record.raw_source_json;
        return JSON.stringify(parsed, null, 2);
      } catch (_) { return String(this.record.raw_source_json); }
    },
    crmFieldOptions() {
      return buildCrmRemapOptions(this.$t.bind(this));
    },
    hasRemapSelections() {
      return Object.values(this.remapSelections).some((v) => v);
    },
    // true dacă userul a schimbat cel puțin o mapare existentă
    hasMappingChanges() {
      return Object.entries(this.mappingChanges).some(([src, newTarget]) => {
        const currentTarget = this.mappingAuditEntries.find((e) => e.source === src)?.target;
        return newTarget && newTarget !== currentTarget;
      });
    },
    canRunDedup() {
      return this.record?.status === "pending_dedup";
    },
    canRecheckDedupAfterEnrichment() {
      return !this.isReadOnly && ["pending_review", "approved"].includes(this.record?.status);
    },
    canReview() {
      return ["pending_review", "duplicate_found", "pending_enrichment"].includes(
        this.record?.status
      );
    },
    canConfirmExistingCompany() {
      return this.canReview && this.dedupCandidates.length > 0;
    },
    canConfirmNewCompany() {
      return this.canReview;
    },
    canWrite() {
      return this.record?.status === "approved";
    },
    writtenCompanyId() {
      const id = Number(this.record?.written_company_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    canOpenWrittenCompany() {
      return Boolean(this.writtenCompanyId);
    },
    isReadOnly() {
      return [
        "written",
        "written_new_company",
        "written_existing_company",
        "lead_board_added",
        "lead_board_already_exists",
      ].includes(this.record?.status);
    },
    writeActionLabel() {
      return this.record?.matched_company_id
        ? this.$t("ncrm.workflows.staging.actions.updateExistingCompany")
        : this.$t("ncrm.workflows.staging.actions.createNewCompany");
    },
  },
  watch: {
    record: {
      immediate: true,
      handler() {
        this.remapSelections = {};
        this.mappingChanges = Object.fromEntries(this.mappingAuditEntries.map((entry) => [entry.source, entry.target]));
        this.selectedCandidateId = this.resolveInitialCandidateId();
        this.notesInput = this.record?.review_notes || "";
        this.showRawJson = false;
        this.resetAiSuggestionState();
      },
    },
  },
  methods: {
    formatFieldValue(key, value) {
      if (value === null || value === undefined || value === "") return null;
      if (key === "business_is_active") {
        return Number(value) === 1
          ? this.$t("ncrm.contacts.details.businessStatus.active")
          : this.$t("ncrm.contacts.details.businessStatus.inactive");
      }
      return String(value);
    },
    formatSuggestionValue(value) {
      if (value === null || value === undefined || value === "") return "";
      if (typeof value === "boolean") return value ? "true" : "false";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    formatConfidence(value) {
      if (!Number.isFinite(Number(value))) return this.$t("ncrm.workflows.staging.drawer.confidenceUnknown");
      return `${Math.round(Number(value) * 100)}%`;
    },
    resetAiSuggestionState() {
      const selections = {};
      const edits = {};
      this.enrichmentSuggestionRows.forEach((suggestion) => {
        selections[suggestion.field] = suggestion.decision !== "approved" && suggestion.decision !== "rejected";
        edits[suggestion.field] = suggestion.valueLabel;
      });
      this.suggestionSelections = selections;
      this.suggestionEdits = edits;
    },
    resolveInitialCandidateId() {
      const candidates = this.dedupCandidates;
      if (!candidates.length) return null;
      const matchedId = Number(this.record?.matched_company_id);
      if (Number.isFinite(matchedId) && candidates.some((candidate) => candidate.id === matchedId)) {
        return matchedId;
      }
      return candidates[0]?.id || null;
    },
    openWrittenCompany() {
      if (!this.writtenCompanyId) return;
      this.$emit("update:modelValue", false);
      this.$router.push({
        name: "NcrmCompanyDetails",
        params: { id: this.writtenCompanyId },
      });
    },
    selectCandidate(candidateId) {
      if (this.isReadOnly) return;
      this.selectedCandidateId = candidateId;
    },
    candidateEvidence(candidate) {
      const fields = candidate?.matched_fields || {};
      return Object.entries(fields)
        .map(([key, field]) => {
          const score = Math.round(Number(field?.score || 0));
          const weight = Math.round(Number(field?.weight || 0));
          const contribution = Math.round((score * weight) / 100);
          return {
            key,
            label: field?.label || key,
            score,
            contribution,
            stagingField: field?.staging_field || "-",
            companyField: field?.company_field || "-",
            stagingValue: this.formatEvidenceValues(field?.staging_values),
            companyValue: this.formatEvidenceValues(field?.company_values),
          };
        })
        .filter((field) => field.contribution > 0)
        .sort((a, b) => b.contribution - a.contribution || b.score - a.score);
    },
    formatEvidenceValues(values) {
      const list = Array.isArray(values) ? values : [];
      const compact = list.map((value) => String(value || "").trim()).filter(Boolean);
      if (!compact.length) return "—";
      return compact.slice(0, 3).join(" / ");
    },
    statusChip(status) {
      const map = {
        pending_dedup:      { color: "neutral",  variant: "tonal" },
        duplicate_found:    { color: "warning",  variant: "tonal" },
        pending_enrichment: { color: "info",     variant: "tonal" },
        enriched_pending_dedup: { color: "info", variant: "tonal" },
        pending_review:     { color: "warning",  variant: "tonal" },
        approved:           { color: "success",  variant: "tonal" },
        rejected:           { color: "error",    variant: "tonal" },
        written:            { color: "success",  variant: "flat"  },
        written_new_company: { color: "success", variant: "flat" },
        written_existing_company: { color: "success", variant: "tonal" },
        lead_board_added: { color: "success", variant: "flat" },
        lead_board_already_exists: { color: "info", variant: "tonal" },
        lead_board_error: { color: "error", variant: "tonal" },
      };
      return map[status] || { color: "neutral", variant: "tonal" };
    },
    mappingChip(status) {
      return {
        auto_mapped:       { color: "neutral",  variant: "tonal" },
        needs_review:      { color: "warning",  variant: "tonal" },
        mapping_confirmed: { color: "success",  variant: "tonal" },
      }[status] || { color: "neutral", variant: "tonal" };
    },
    dedupScoreColor(score) {
      if (score >= 95) return "success";
      if (score >= 70) return "warning";
      return "error";
    },
    submitAction(action) {
      if (this.isReadOnly) return;

      if (action === "rejected") {
        this.$emit("reject", {
          record: this.record,
          status: "rejected",
          review_notes: this.notesInput || undefined,
        });
        return;
      }

      this.$emit("approve", {
        record: this.record,
        status: "approved",
        matched_company_id:
          action === "approved-existing"
            ? this.selectedCandidateId || null
            : null,
        review_notes: this.notesInput || undefined,
      });
    },
    // Remapare câmpuri nemapate (din secțiunea "Câmpuri nemapate")
    submitRemap() {
      if (this.isReadOnly) return;

      // Protocol: { sourceField: targetCrmColumn }
      const fieldUpdates = {};
      for (const [srcKey, crmCol] of Object.entries(this.remapSelections)) {
        if (crmCol) fieldUpdates[srcKey] = crmCol;
      }
      const confirmedUnmapped = this.unmappedEntries
        .map((e) => e.key)
        .filter((k) => !this.remapSelections[k]);
      this.$emit("remap", {
        record: this.record,
        field_updates: fieldUpdates,
        confirmed_unmapped: confirmedUnmapped,
      });
    },

    // Salvare modificări pe câmpuri deja mapate (din coloana "Mapare sursă")
    submitAllRemaps() {
      if (this.isReadOnly) return;

      const fieldUpdates = {};
      for (const [src, newTarget] of Object.entries(this.mappingChanges)) {
        const currentTarget = this.mappingAuditEntries.find((e) => e.source === src)?.target;
        if (newTarget && newTarget !== currentTarget) {
          fieldUpdates[src] = newTarget;
        }
      }
      if (!Object.keys(fieldUpdates).length) return;
      this.$emit("remap", {
        record: this.record,
        field_updates: fieldUpdates,
        confirmed_unmapped: [],
      });
    },
    submitAiSuggestionDecisions(status) {
      if (this.isReadOnly) return;
      const decisions = this.enrichmentSuggestionRows
        .filter((suggestion) => this.suggestionSelections[suggestion.field])
        .map((suggestion) => ({
          field_key: suggestion.field,
          decision: status,
          value: this.suggestionEdits[suggestion.field],
        }));
      if (!decisions.length) return;
      this.$emit("review-enrichment", {
        record: this.record,
        decisions,
      });
    },
  },
};
</script>

<style scoped>
.ncrm-staging-drawer__header {
  background: rgb(var(--v-theme-surface));
}

.ncrm-staging-drawer__section {
  background: rgb(var(--v-theme-surface));
}

.ncrm-staging-drawer__columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: rgb(var(--v-theme-surface));
  border-bottom: none;
}

.ncrm-staging-drawer__col {
  min-width: 0;
}

.ncrm-staging-drawer__col + .ncrm-staging-drawer__col {
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Field rows */
.ncrm-staging-drawer__field {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 6px;
  align-items: baseline;
  padding: 3px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.05);
}

.ncrm-staging-drawer__field:last-child { border-bottom: none; }

.ncrm-staging-drawer__field-key {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--app-text-default);
  opacity: 0.55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-staging-drawer__field-value {
  font-size: 0.82rem;
  color: var(--ncrm-color-text-strong);
  word-break: break-word;
}

.ncrm-staging-drawer__field-empty {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.3;
}

/* Mapping audit rows (editabile) */
.ncrm-staging-drawer__mapping-edit-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.05);
  min-height: 30px;
}

.ncrm-staging-drawer__mapping-edit-row:last-of-type { border-bottom: none; }

.ncrm-staging-drawer__mapping-src {
  flex-shrink: 0;
  width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* v-select în modul plain, inline în rândul de mapare */
.ncrm-staging-drawer__mapping-select {
  min-width: 0;
  flex: 1;
}

.ncrm-staging-drawer__mapping-select :deep(.v-field__input) {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--ncrm-color-text-strong);
  padding-top: 0;
  padding-bottom: 0;
  min-height: 28px;
}

.ncrm-staging-drawer__mapping-select :deep(.v-field__append-inner) {
  padding-top: 0;
}

.ncrm-staging-drawer__mapping-target {
  color: var(--ncrm-color-text-strong);
  font-weight: 500;
}

/* Notes */
.ncrm-staging-drawer__notes-content {
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(var(--v-theme-on-surface), 0.02);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.82rem;
  line-height: 1.5;
  max-height: 180px;
  overflow-y: auto;
}

/* Dedup candidates */
.ncrm-staging-drawer__candidate {
  padding: 8px 10px;
  border-radius: var(--app-radius-sm, 6px);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.ncrm-staging-drawer__candidate:hover,
.ncrm-staging-drawer__candidate:focus-visible {
  border-color: rgba(var(--v-theme-primary), 0.35);
  outline: none;
}

.ncrm-staging-drawer__candidate--selected {
  border-color: rgba(var(--v-theme-primary), 0.58);
  background: rgba(var(--v-theme-primary), 0.06);
}

.ncrm-staging-drawer__candidate--readonly {
  cursor: default;
}

.ncrm-staging-drawer__candidate--readonly:hover,
.ncrm-staging-drawer__candidate--readonly:focus-visible {
  border-color: rgba(var(--v-theme-on-surface), 0.08);
}

.ncrm-staging-drawer__candidate--readonly.ncrm-staging-drawer__candidate--selected:hover,
.ncrm-staging-drawer__candidate--readonly.ncrm-staging-drawer__candidate--selected:focus-visible {
  border-color: rgba(var(--v-theme-primary), 0.58);
}

.ncrm-staging-drawer__candidate-evidence {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 9px;
  padding-left: 26px;
}

.ncrm-staging-drawer__candidate-field {
  padding: 8px 10px;
  border-radius: var(--app-radius-sm, 6px);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgb(var(--v-theme-surface));
}

.ncrm-staging-drawer__candidate-field-label {
  min-width: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ncrm-color-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-staging-drawer__candidate-field-score {
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--ncrm-color-text-strong);
  opacity: 0.72;
}

.ncrm-staging-drawer__candidate-values {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 14px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  margin-top: 6px;
  font-size: 0.78rem;
  line-height: 1.35;
  color: var(--ncrm-color-text-strong);
}

.ncrm-staging-drawer__candidate-values span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-staging-drawer__candidate-empty {
  margin: 6px 0 0 26px;
  font-size: 0.68rem;
  color: var(--app-text-default);
  opacity: 0.55;
}

/* AI suggestions */
.ncrm-staging-drawer__ai-suggestion {
  padding: 10px 12px;
  border-radius: var(--app-radius-sm, 6px);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgba(var(--v-theme-primary), 0.035);
}

.ncrm-staging-drawer__ai-suggestion--readonly {
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.ncrm-staging-drawer__ai-field {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--ncrm-color-text-strong);
}

.ncrm-staging-drawer__ai-values {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.ncrm-staging-drawer__ai-value-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--app-text-default);
  opacity: 0.62;
}

.ncrm-staging-drawer__ai-value {
  display: block;
  margin-top: 4px;
  font-size: 0.82rem;
  color: var(--ncrm-color-text-strong);
  word-break: break-word;
}

.ncrm-staging-drawer__ai-reason {
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--app-text-default);
  opacity: 0.78;
}

/* Unmapped */
.ncrm-staging-drawer__unmapped-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: center;
  padding: 4px 0;
}

.ncrm-staging-drawer__unmapped-key {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Raw JSON */
.ncrm-staging-drawer__raw-pre {
  font-size: 0.72rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 6px;
  padding: 10px;
  max-height: 240px;
  overflow-y: auto;
  color: var(--app-text-default);
}

.ncrm-staging-drawer__footer {
  background: rgb(var(--v-theme-surface));
}

.cursor-pointer { cursor: pointer; }
</style>
